// Sheet mapping persistence model (SaveSheetMappings/GetSheetMappings). A "sheet mapping" is one
// sheet-to-worksheet association the user made on the Upload step: which uploaded file (and, for
// Excel, which workbook sheet) feeds which template worksheet. Worksheet types are the BE's reference
// list for template worksheets — the FE resolves them by name because it only knows the TemplateType
// string enum, not the numeric templateTypeID.

import {
  NewSheetMapping,
  SheetMappingModel as GeneratedSheetMappingModel,
  WorksheetTypeModel,
} from "services/DataImportService";
import type { UploadState } from "./steps/FileUpload/FileUploadSlice";
import {
  groupSheetLinksByWorksheet,
  listLinkedSheets,
  listSheetSources,
  SheetSource,
  SheetTarget,
  WorkbookSheetEntry,
} from "./sheetSources";
import { matchWorksheetBySheetName, UploadFileStatus, UploadMode } from "./steps/FileUpload/uploadRules";

// Module-owned sheet mapping model, normalised the same way submissionModel.ts normalises submissions:
// fields the contract guarantees become required, everything stays serialisable for Redux.
export type SheetMapping = {
  submissionSheetMappingID: string;
  submissionFileID: string;
  // Template worksheet name (the worksheet type's name) — the key the mapping and validation
  // steps use everywhere.
  worksheet: string;
  worksheetTypeID: number;
  sourceSheetName?: string;
  sourceOrder: number;
  joinKeyColumnName: string | null;
  columnMappingsConfirmed: boolean;
};

// Serialisable subset of the worksheet types reference list.
export type WorksheetTypeEntry = {
  worksheetTypeID: number;
  name: string;
};

export function toWorksheetTypeEntry(generated: WorksheetTypeModel): WorksheetTypeEntry {
  const { selfServeDataImportWorksheetTypeID, name } = generated;
  if (selfServeDataImportWorksheetTypeID === undefined || !name) {
    throw new Error("Malformed worksheet type in API response");
  }
  return { worksheetTypeID: selfServeDataImportWorksheetTypeID, name };
}

// Sheet mappings saved before a worksheet could have several sources come back as source 1 with no key
export function toSheetMapping(generated: GeneratedSheetMappingModel): SheetMapping {
  const { submissionSheetMappingID, submissionFileID, selfServeDataImportWorksheetTypeID, worksheetName } = generated;
  if (
    !submissionSheetMappingID ||
    !submissionFileID ||
    selfServeDataImportWorksheetTypeID === undefined ||
    !worksheetName
  ) {
    throw new Error("Malformed sheet mapping in API response");
  }
  return {
    submissionSheetMappingID,
    submissionFileID,
    worksheet: worksheetName,
    worksheetTypeID: selfServeDataImportWorksheetTypeID,
    sourceSheetName: generated.sourceSheetName ?? undefined,
    sourceOrder: generated.sourceOrder ?? 1,
    joinKeyColumnName: generated.joinKeyColumnName ?? null,
    columnMappingsConfirmed: generated.columnMappingsConfirmed ?? false,
  };
}

// Worksheet names are matched ignoring case and punctuation/spacing ("Charge Dates" ==
// "ChargeDates") — the BE reference list and the FE template config are maintained separately,
// so exact string equality is too brittle.
export function normaliseWorksheetName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Maps a BE worksheet name back to the FE template's canonical spelling (loose match — see
// normaliseWorksheetName). null when the offered list doesn't contain it.
export function canonicalWorksheetName(name: string, worksheets: string[]): string | null {
  const target = normaliseWorksheetName(name);
  return worksheets.find((worksheet) => normaliseWorksheetName(worksheet) === target) ?? null;
}

export function findSheetMappingsForWorksheet(sheetMappings: SheetMapping[], worksheet: string): SheetMapping[] {
  const target = normaliseWorksheetName(worksheet);
  return sheetMappings.filter((sheetMapping) => normaliseWorksheetName(sheetMapping.worksheet) === target);
}

// The sheets after the sheet's target worksheets are set to the worksheets given
export function replaceSheetWorksheets(
  sheets: WorkbookSheetEntry[],
  sheetMappings: SheetMapping[],
  sheetName: string,
  worksheets: string[]
): WorkbookSheetEntry[] {
  return sheets.map((sheet) =>
    sheet.sheetName === sheetName
      ? { ...sheet, targets: buildSheetTargets(sheets, sheetMappings, sheetName, worksheets) }
      : sheet
  );
}

// A sheet's targets for the worksheets: existing ones are kept, new ones reuse a saved sheet mapping's order and key
export function buildSheetTargets(
  sheets: WorkbookSheetEntry[],
  sheetMappings: SheetMapping[],
  sheetName: string,
  worksheets: string[]
): SheetTarget[] {
  const currentTargets = sheets.find((sheet) => sheet.sheetName === sheetName)?.targets ?? [];
  return [...new Set(worksheets)].map(
    (worksheet) =>
      currentTargets.find((target) => target.worksheet === worksheet) ??
      buildSheetTarget(sheets, findSheetMappingsForWorksheet(sheetMappings, worksheet), sheetName, worksheet)
  );
}

// The sheet's target for a worksheet, keeping the source order and key it was last saved with.
// A new source takes an order higher than any the worksheet has used, as the API matches sources on it.
export function buildSheetTarget(
  sheets: WorkbookSheetEntry[],
  savedSources: SheetMapping[],
  sheetName: string,
  worksheet: string
): SheetTarget {
  const usedSourceOrders = listLinkedSheets(sheets, worksheet)
    .filter((link) => link.sheetName !== sheetName)
    .map((link) => link.target.sourceOrder);
  const savedSource = savedSources.find((sheetMapping) => sheetMapping.sourceSheetName === sheetName);
  if (savedSource && !usedSourceOrders.includes(savedSource.sourceOrder)) {
    return {
      worksheet,
      sourceOrder: savedSource.sourceOrder,
      keyColumnName: savedSource.joinKeyColumnName,
      matchedBySheetName: false,
    };
  }

  const highestSourceOrder = Math.max(
    0,
    ...usedSourceOrders,
    ...savedSources.map((sheetMapping) => sheetMapping.sourceOrder)
  );
  return { worksheet, sourceOrder: highestSourceOrder + 1, keyColumnName: null, matchedBySheetName: false };
}

// Saved sheet mappings outrank the sheet-name auto-match. The auto-match never adds a sheet to a worksheet
// that already has saved sources.
export function restoreWorkbookSheets(
  sheetNames: string[],
  sheetMappings: SheetMapping[] | null,
  worksheets: string[]
): WorkbookSheetEntry[] {
  const savedTargetsBySheet = new Map<string, SheetTarget[]>();
  for (const sheetMapping of sheetMappings ?? []) {
    const worksheet = canonicalWorksheetName(sheetMapping.worksheet, worksheets);
    if (!sheetMapping.sourceSheetName || !worksheet) continue;
    savedTargetsBySheet.set(sheetMapping.sourceSheetName, [
      ...(savedTargetsBySheet.get(sheetMapping.sourceSheetName) ?? []),
      {
        worksheet,
        sourceOrder: sheetMapping.sourceOrder,
        keyColumnName: sheetMapping.joinKeyColumnName,
        matchedBySheetName: false,
      },
    ]);
  }
  const worksheetsWithSavedSources = new Set(
    [...savedTargetsBySheet.values()].flatMap((targets) => targets.map((target) => target.worksheet))
  );

  return sheetNames.map((sheetName) => {
    const savedTargets = savedTargetsBySheet.get(sheetName);
    if (savedTargets) return { sheetName, targets: savedTargets };

    const matchedWorksheet = matchWorksheetBySheetName(sheetName, worksheets);
    if (!matchedWorksheet || worksheetsWithSavedSources.has(matchedWorksheet)) return { sheetName, targets: [] };
    return {
      sheetName,
      targets: [{ worksheet: matchedWorksheet, sourceOrder: 1, keyColumnName: null, matchedBySheetName: true }],
    };
  });
}

// The sheets the upload links to worksheets.
// A resumed upload hasn't read them, so they're restored from the saved sheet mappings.
export async function resolveSheetSources(
  sheets: WorkbookSheetEntry[] | undefined,
  sheetMappings: SheetMapping[] | null,
  worksheets: string[],
  readSheetNames: () => Promise<string[]>
): Promise<SheetSource[]> {
  if (sheets) return listSheetSources(sheets);
  return listSheetSources(restoreWorkbookSheets(await readSheetNames(), sheetMappings, worksheets));
}

// FE template names stay canonical; worksheets either side doesn't know are silently not offered.
export function availableWorksheets(templateWorksheets: string[], worksheetTypes: WorksheetTypeEntry[]): string[] {
  const known = new Set(worksheetTypes.map((entry) => normaliseWorksheetName(entry.name)));
  return templateWorksheets.filter((worksheet) => known.has(normaliseWorksheetName(worksheet)));
}

// Builds the SaveSheetMappings payload. Sheets and files set to "Not provided" are left out.
// A key column is only sent for a worksheet with more than one source.
export function buildSheetMappingsFromUploadState(
  uploadState: UploadState,
  worksheetTypes: WorksheetTypeEntry[]
): NewSheetMapping[] {
  if (uploadState.mode === UploadMode.Excel) {
    const workbook = uploadState.files[0];
    if (!workbook?.submissionFileID) return [];
    const sheets = uploadState.sheets ?? [];
    const linksByWorksheet = groupSheetLinksByWorksheet(sheets);
    return sheets.flatMap(({ sheetName, targets }) =>
      targets.map((target) => ({
        submissionFileID: workbook.submissionFileID,
        selfServeDataImportWorksheetTypeID: resolveWorksheetTypeID(target.worksheet, worksheetTypes),
        sourceSheetName: sheetName,
        sourceOrder: target.sourceOrder,
        joinKeyColumnName:
          (linksByWorksheet.get(target.worksheet)?.length ?? 0) > 1 ? target.keyColumnName ?? undefined : undefined,
      }))
    );
  }

  return uploadState.files
    .filter(
      (file) => file.status === UploadFileStatus.Scanned && file.submissionFileID && file.targetWorksheet !== null
    )
    .map((file) => ({
      submissionFileID: file.submissionFileID,
      selfServeDataImportWorksheetTypeID: resolveWorksheetTypeID(file.targetWorksheet as string, worksheetTypes),
    }));
}

function resolveWorksheetTypeID(worksheet: string, worksheetTypes: WorksheetTypeEntry[]): number {
  const target = normaliseWorksheetName(worksheet);
  const match = worksheetTypes.find((entry) => normaliseWorksheetName(entry.name) === target);
  if (!match) {
    // The available names ride along so a seed-data gap on the BE is diagnosable from the toast.
    const available = worksheetTypes.map((entry) => entry.name).join(", ") || "none";
    throw new Error(`No worksheet type matches "${worksheet}" (backend has: ${available})`);
  }
  return match.worksheetTypeID;
}
